import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { useSession } from '@/shared/hooks/useSession'
import type { InstallationWithResources } from '@/features/installations/api/installation-model'
import type { InstallationCreateApiPayload } from '@/features/installations/installation-create.schema'

export const INSTALLATIONS_QUERY_KEY = 'installations'

export const useInstallations = () => {
  const queryClient = useQueryClient()
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  return useQuery<InstallationWithResources[]>({
    queryKey: [INSTALLATIONS_QUERY_KEY, organizationId],
    queryFn: async () => {
      const res = await apiClient.get<{
        installations: InstallationWithResources[]
      }>(`/v1/organizations/${organizationId}/installations`)

      for (const installation of res.data.installations) {
        queryClient.setQueryData<InstallationWithResources>(
          [INSTALLATIONS_QUERY_KEY, organizationId, installation.id],
          installation
        )
      }

      return res.data.installations
    },
    staleTime: Infinity,
  })
}

export const useCreateInstallation = () => {
  const queryClient = useQueryClient()
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()

  return useMutation({
    mutationFn: async (payload: InstallationCreateApiPayload) => {
      const res = await apiClient.post<InstallationWithResources>(
        `/v1/organizations/${organizationId}/installations`,
        payload
      )
      return res.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [INSTALLATIONS_QUERY_KEY, organizationId],
      })
    },
  })
}
